/**
 * Utility functions for extracting error messages from Cognito/AWS Amplify errors
 * Based on cognito_error_handling.md patterns
 */

export interface CognitoError {
  message?: string;
  __type?: string;
  name?: string;
  code?: string;
  error?: string;
  errorMessage?: string;
  Message?: string;
  underlyingError?: any;
  toString?: () => string;
}

/**
 * Extracts error message from Cognito/AWS Amplify errors
 * Checks multiple possible locations where the error message might be stored
 * 
 * @param error - The error object from Cognito/Amplify
 * @param defaultMessage - Default message if no error message is found
 * @returns The extracted error message
 */
export function extractCognitoErrorMessage(
  error: unknown,
  defaultMessage: string = 'An authentication error occurred.'
): string {
  if (!error) {
    return defaultMessage;
  }

  // Type guard for error-like objects
  const err = error as CognitoError;

  // Log full error structure for debugging (in development only)
  if (import.meta.env.DEV) {
    console.log('Extracting Cognito error message from:', {
      message: err.message,
      __type: err.__type,
      name: err.name,
      code: err.code,
      error: err.error,
      errorMessage: err.errorMessage,
      Message: err.Message,
      keys: Object.keys(err),
      fullError: error,
    });
  }

  // Try different error message locations (in order of likelihood)
  // Check case-sensitive variations
  if (err.message) {
    return err.message;
  }

  if (err.Message) {
    return err.Message;
  }

  if (err.errorMessage) {
    return err.errorMessage;
  }

  if (err.error) {
    return err.error;
  }

  // Check for specific Cognito error types
  if (err.__type === 'InvalidLambdaResponseException') {
    // Lambda exception - message should be in error.message or error.Message
    return err.message || err.Message || 'Service accounts are not allowed to sign up.';
  }

  if (err.name === 'InvalidLambdaResponseException') {
    return err.message || err.Message || 'Service accounts are not allowed to sign up.';
  }

  if (err.__type === 'InvalidParameterException') {
    return err.message || err.Message || 'Invalid authentication parameters.';
  }

  if (err.__type === 'UsernameExistsException') {
    return err.message || err.Message || 'An account with this email already exists.';
  }

  if (err.__type === 'NotAuthorizedException') {
    return err.message || err.Message || 'Authentication failed. Please check your credentials.';
  }

  // Check if error is a string
  if (typeof error === 'string') {
    return error;
  }

  // Check if error has a toString method
  if (err && typeof err.toString === 'function') {
    try {
      const stringError = err.toString();
      if (stringError && stringError !== '[object Object]' && stringError !== 'Error') {
        return stringError;
      }
    } catch {
      // Ignore toString errors
    }
  }

  // Fallback to default message
  return defaultMessage;
}

/**
 * Determines if an error is a service account rejection error
 * Checks for patterns in error messages that indicate service account rejection
 * 
 * @param error - The error object
 * @returns true if the error indicates a service account rejection
 */
export function isServiceAccountRejectionError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const errorMessage = extractCognitoErrorMessage(error, '').toLowerCase();

  // Check for service account patterns in error messages
  const serviceAccountPatterns = [
    'service account',
    'service accounts',
    'active directory service account',
    'hr service account',
    'aws service account',
    'not allowed for',
    'sign-up is not allowed',
    'login is not allowed',
    'service account logins',
  ];

  return serviceAccountPatterns.some(pattern => errorMessage.includes(pattern));
}

/**
 * Extracts specific service account type from error message
 * 
 * @param error - The error object
 * @returns The service account type ('ad', 'hr', 'aws', or null)
 */
export function getServiceAccountType(error: unknown): 'ad' | 'hr' | 'aws' | null {
  if (!error) {
    return null;
  }

  const errorMessage = extractCognitoErrorMessage(error, '').toLowerCase();

  if (errorMessage.includes('active directory')) {
    return 'ad';
  }

  if (errorMessage.includes('hr service')) {
    return 'hr';
  }

  if (errorMessage.includes('aws service')) {
    return 'aws';
  }

  return null;
}

/**
 * Checks if an error is a Cognito-related error
 * 
 * @param error - The error object
 * @returns true if the error appears to be from Cognito
 */
export function isCognitoError(error: unknown): boolean {
  if (!error) {
    return false;
  }

  const err = error as CognitoError;
  
  return !!(
    err.__type ||
    err.name?.includes('Cognito') ||
    err.name?.includes('Lambda') ||
    err.code?.includes('Cognito') ||
    err.code?.includes('Lambda')
  );
}

/**
 * Formats a user-friendly error message from Cognito errors
 * Handles service account rejections and other common Cognito errors
 * 
 * @param error - The error object
 * @returns A formatted error message for display
 */
export function formatCognitoErrorForDisplay(error: unknown): string {
  if (!error) {
    return 'An authentication error occurred.';
  }

  // Check if it's a service account rejection
  if (isServiceAccountRejectionError(error)) {
    const baseMessage = extractCognitoErrorMessage(error);
    
    // If the error message already contains a good message, use it
    if (baseMessage && !baseMessage.includes('InvalidLambdaResponseException')) {
      return baseMessage;
    }

    // Otherwise, provide a generic service account message
    return 'Service account logins are not permitted. Please use your standard TNC login email address.';
  }

  // For other errors, extract and return the message
  return extractCognitoErrorMessage(error);
}

